import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, FileText, Activity, Inbox, Settings, LogOut,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const linkBase =
  "flex items-center gap-3 px-3 py-2.5 rounded-lg font-mono text-[11px] tracking-[0.12em] uppercase transition-colors";

function navClass({ isActive }) {
  return `${linkBase} ${isActive ? "bg-[#E7F1F8] text-[#0C2A40]" : "text-[#51667A] hover:bg-[#F2F8FC]"}`;
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = (user?.name || "A").split(" ").map((n) => n[0]).slice(0, 2).join("");

  const links = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/dashboard/ehr", label: "EHR + Scribe", icon: FileText },
    { to: "/dashboard/rcm", label: "RCM Claims", icon: Activity },
  ];
  if (user?.role === "admin") links.push({ to: "/dashboard/leads", label: "Demo Requests", icon: Inbox });

  return (
    <div className="min-h-screen bg-[#F2F8FC] flex" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-[#D8E6F1] p-6 sticky top-0 h-screen">
        <div className="mb-10">
          <img src="/astra-logo.png" alt="Astra" className="h-12 w-auto" />
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink key={l.to} to={l.to} end={l.end} className={navClass} data-testid={`nav-${l.label.toLowerCase().split(" ")[0]}`}>
                <Icon size={16} /> {l.label}
              </NavLink>
            );
          })}
          <span className={`${linkBase} text-[#51667A] hover:bg-[#F2F8FC] cursor-pointer`}>
            <Settings size={16} /> Settings
          </span>
        </nav>
        <button
          data-testid="dashboard-logout"
          onClick={handleLogout}
          className={`${linkBase} text-[#51667A] hover:text-[#2880B8] hover:bg-[#F2F8FC]`}
        >
          <LogOut size={16} /> Log Out
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center justify-between px-6 md:px-10 h-20 bg-white border-b border-[#D8E6F1] sticky top-0 z-30">
          <div>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#93A5B5]">Workspace</p>
            <p className="font-display text-lg font-semibold text-[#0C2A40]">{user?.company || "Astra Practice"}</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="dashboard-logout-mobile" onClick={handleLogout} className="md:hidden text-[#51667A]"><LogOut size={18} /></button>
            <div className="w-10 h-10 rounded-full bg-[#2880B8] flex items-center justify-center font-mono text-sm font-bold text-white">
              {initials}
            </div>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden flex gap-2 overflow-x-auto px-4 py-3 bg-white border-b border-[#D8E6F1]">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `whitespace-nowrap px-3 py-2 rounded-lg font-mono text-[10px] tracking-[0.12em] uppercase ${isActive ? "bg-[#E7F1F8] text-[#0C2A40]" : "text-[#51667A] bg-[#F2F8FC]"}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="p-6 md:p-10 flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
